import type { AxiosError } from 'axios';
import type { QueryClient } from '@tanstack/react-query';
import { processDetailQueryKey } from '../../hooks/useProcessDetail';
import type { ProcessRunSessionState, SafeSetState } from './types';

interface ApiErrorBody {
  readonly error?: string;
  readonly message?: string;
  readonly details?: Readonly<Record<string, unknown>>;
}

export const STALE_GRAPH_MESSAGE =
  'This process changed while the dialog was open. We loaded the latest '
  + 'version — check your inputs and start again.';

export const errorCode = (err: unknown): string | null => {
  const ax = err as AxiosError<ApiErrorBody> | undefined;
  if (ax?.isAxiosError !== true) return null;
  return ax.response?.data?.error ?? null;
};

export const errorMessage = (err: unknown, fallback: string): string => {
  const ax = err as AxiosError<ApiErrorBody> | undefined;
  return ax?.response?.data?.message ?? (err instanceof Error ? err.message : fallback);
};

/**
 * `VALIDATION_FAILED` + a `portId` detail is the untokened form of the same
 * condition: the only two backend sites pairing that code with that key are
 * the dead-port throws in `runs:prepare` and `runs:start`.
 */
const isStaleGraphError = (err: unknown, code: string | null): boolean => {
  if (code === 'DRIFT_DETECTED') return true;
  if (code !== 'VALIDATION_FAILED') return false;
  const ax = err as AxiosError<ApiErrorBody> | undefined;
  return typeof ax?.response?.data?.details?.portId === 'string';
};

/**
 * Shared error-to-UX translator for the start run pair. Both phases can
 * fail with the same stable backend codes, so funnelling them through one
 * helper keeps the snackbar copy and inputError fold consistent.
 *
 * `onStaleGraph` must reconverge through the graph-sync hook, never a bare
 * `invalidateQueries`: only the hook rebaselines auto-save alongside the
 * reseed, and without that the user's next edit 409s into the drift modal.
 */
export const handleStartError = (
  err: unknown,
  fallback: string,
  safeSetState: SafeSetState,
  queryClient: QueryClient,
  processId: string,
  showErrorRef: React.MutableRefObject<(msg: string) => void>,
  onStaleGraph?: () => void,
): void => {
  const code = errorCode(err);
  const msg = errorMessage(err, fallback);
  if (isStaleGraphError(err, code)) {
    showErrorRef.current(STALE_GRAPH_MESSAGE);
    onStaleGraph?.();
  } else if (code === 'TEST_RUN_ACTIVE') {
    showErrorRef.current('A test run is already in progress for this process.');
    queryClient
      .invalidateQueries({ queryKey: processDetailQueryKey(processId) })
      .catch(() => undefined);
  } else if (code === 'INSUFFICIENT_CREDITS') {
    showErrorRef.current('Not enough credits to start this run.');
  } else if (code === 'PROCESS_NOT_OWNER') {
    showErrorRef.current('Only the owner can run a draft process.');
  } else if (code === 'MISSING_REQUIRED_INPUT' || code === 'INPUT_TYPE_MISMATCH') {
    const ax = err as AxiosError<ApiErrorBody>;
    const missingRaw = ax.response?.data?.details?.missingInputs;
    const missingInputs = Array.isArray(missingRaw)
      ? missingRaw.filter((x): x is string => typeof x === 'string')
      : [];
    safeSetState((prev: ProcessRunSessionState) => ({
      ...prev,
      inputError: { message: msg, missingInputs },
    }));
  } else {
    showErrorRef.current(msg);
  }
  safeSetState((prev: ProcessRunSessionState) => ({ ...prev, startInFlight: false }));
};

/**
 * Shared error-to-UX translator for the manual-node completion pair
 * (prepare + complete). Non-assignees get a clear 403 message; a stale wait
 * (already completed / timed out) clears the prompt and refetches so the
 * canvas catches up instead of leaving a dead dialog open.
 */
export const handleManualError = (
  err: unknown,
  safeSetState: SafeSetState,
  queryClient: QueryClient,
  processId: string,
  showErrorRef: React.MutableRefObject<(msg: string) => void>,
): void => {
  const code = errorCode(err);
  if (code === 'INSUFFICIENT_ROLE') {
    showErrorRef.current('Only an assigned user can complete this manual step.');
  } else if (
    code === 'RUN_NOT_WAITING' ||
    code === 'ITERATION_MISMATCH' ||
    code === 'NODE_NOT_FOUND'
  ) {
    safeSetState((prev: ProcessRunSessionState) => ({ ...prev, manualWaiting: null }));
    queryClient
      .invalidateQueries({ queryKey: processDetailQueryKey(processId) })
      .catch(() => undefined);
  } else if (code === 'UPLOAD_TOO_LARGE') {
    showErrorRef.current('One of the uploaded files exceeds the maximum allowed size.');
  } else {
    showErrorRef.current(errorMessage(err, 'Failed to complete the manual step'));
  }
};
